import "../../assets/dragSelect.css";
import React from "react";
import {toast} from "react-toastify";
import {useAtom} from "jotai";
import styled from "styled-components";
import {
  timeBudgetDetailDataAtom,
  timesInfo,
  weeksInfo
} from "../../pages/settings/entity/BudgetTime";

const MAX_PERCENT = 100;

const createDefaultAllowTimes = () =>
    Array.from(
        {length: 7},
        () =>
            Array.from(
                {length: 24},
                () => ""
            )
    );

export default function InsertToSelect({
  checkWeek = null,
  readOnly = false
}) {
  const [
    timeBudgetDetailDataState,
    setTimeBudgetDetailDataState
  ] = useAtom(timeBudgetDetailDataAtom);

  const allowTimes =
      timeBudgetDetailDataState?.allowTimes ??
      createDefaultAllowTimes();

  const handleChangeInput = (
      weekIndex,
      timeIndex,
      value
  ) => {
    if (readOnly) {
      return;
    }

    /*
     * 빈 값, 정수, 소수점 둘째 자리까지 허용한다.
     */
    const validNumberPattern =
        /^\d{0,3}(\.\d{0,2})?$/;

    if (!validNumberPattern.test(value)) {
      toast.info(
          "숫자만 입력해주세요.",
          {
            toastId:
                "budgetTimeOnlyNumber"
          }
      );

      return;
    }

    if (
        value !== "" &&
        Number(value) > MAX_PERCENT
    ) {
      toast.info(
          "100 이하의 숫자만 입력할 수 있습니다.",
          {
            toastId:
                "budgetTimeMaxPercent"
          }
      );

      return;
    }

    setTimeBudgetDetailDataState(
        prev => ({
          ...prev,
          allowTimes:
              (
                  prev?.allowTimes ??
                  createDefaultAllowTimes()
              ).map(
                  (weekRow, rowIndex) =>
                      rowIndex === weekIndex
                          ? weekRow.map(
                              (
                                  currentValue,
                                  columnIndex
                              ) =>
                                  columnIndex ===
                                  timeIndex
                                      ? value
                                      : currentValue
                          )
                          : weekRow
              )
        })
    );
  };

  const getWeekTotal = weekRow =>
      (weekRow ?? []).reduce(
          (sum, value) => {
            const numberValue =
                Number.parseFloat(value);

            return (
                sum +
                (
                    Number.isNaN(
                        numberValue
                    )
                        ? 0
                        : numberValue
                )
            );
          },
          0
      );

  return (
      <DirectBudgetArea>
        <DirectBudgetGuide>
          각 요일의 시간별 예산 합계가
          100%가 되도록 입력해주세요.
        </DirectBudgetGuide>

        <TableScrollArea>
          <DirectBudgetTable>
            <thead>
            <tr>
              <CornerHeader>
                요일
              </CornerHeader>

              {timesInfo.map(
                  (data, index) => (
                      <TimeHeader
                          key={
                            `${data.time}-${index}`
                          }
                      >
                        <TimeValue>
                          {data.time}
                          {data.label}
                        </TimeValue>
                      </TimeHeader>
                  )
              )}
            </tr>
            </thead>

            <tbody>
            {allowTimes.map(
                (weekRow, weekIndex) => {
                  const weekTotal =
                      getWeekTotal(
                          weekRow
                      );

                  const hasValue =
                      weekTotal > 0;

                  const isValid =
                      weekTotal === 100;

                  const isError =
                      checkWeek ===
                      weekIndex;

                  return (
                      <BudgetWeekRow
                          key={weekIndex}
                          $invalid={isError}
                      >
                        <WeekHeader>
                          <WeekName>
                            {weeksInfo[
                                weekIndex
                                ]?.week ?? "-"}
                          </WeekName>

                          <WeekTotal
                              $valid={
                                isValid
                              }
                              $hasValue={
                                hasValue
                              }
                          >
                            {Number.isInteger(
                                weekTotal
                            )
                                ? weekTotal
                                : weekTotal.toFixed(
                                    2
                                )}
                            %
                          </WeekTotal>
                        </WeekHeader>

                        {weekRow.map(
                            (
                                value,
                                timeIndex
                            ) => (
                                <BudgetCell
                                    key={
                                      `${weekIndex}-${timeIndex}`
                                    }
                                    $readOnly={
                                      readOnly
                                    }
                                >
                                  <PercentInput
                                      type="text"
                                      inputMode="decimal"
                                      maxLength={6}
                                      readOnly={
                                        readOnly
                                      }
                                      aria-label={`${
                                          weeksInfo[
                                              weekIndex
                                              ]?.week ?? ""
                                      } ${
                                          timesInfo[
                                              timeIndex
                                              ]?.time ?? ""
                                      } 예산 비율`}
                                      value={
                                        value === 0
                                            ? ""
                                            : value ?? ""
                                      }
                                      onFocus={
                                        event =>
                                            event.target.select()
                                      }
                                      onChange={
                                        event =>
                                            handleChangeInput(
                                                weekIndex,
                                                timeIndex,
                                                event.target
                                                    .value
                                            )
                                      }
                                  />

                                  <PercentUnit>
                                    %
                                  </PercentUnit>
                                </BudgetCell>
                            )
                        )}
                      </BudgetWeekRow>
                  );
                }
            )}
            </tbody>
          </DirectBudgetTable>
        </TableScrollArea>

        <BudgetStatusGuide>
          <StatusItem>
            <StatusMark
                $type="valid"
            />
            합계 100%
          </StatusItem>

          <StatusItem>
            <StatusMark
                $type="invalid"
            />
            합계 확인 필요
          </StatusItem>
        </BudgetStatusGuide>
      </DirectBudgetArea>
  );
}

const DirectBudgetArea = styled.div`
  width: 100%;
`;

const DirectBudgetGuide = styled.div`
  margin-bottom: 10px;
  color: #888;
  font-size: var(--font-size-14);
`;

const TableScrollArea = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;

  scrollbar-width: thin;
  scrollbar-color:
    #c7c7c7 transparent;

  &::-webkit-scrollbar {
    height: 7px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background-color: #c7c7c7;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #aaa;
  }
`;

const DirectBudgetTable = styled.table`
  width: 100%;
  min-width: 1180px;
  border-spacing: 3px;
  border-collapse: separate;
  table-layout: fixed;
  padding: 4px;
  background-color: #fff;

  th,
  td {
    box-sizing: border-box;
  }
`;

const CornerHeader = styled.th`
  position: sticky;
  left: 0;
  z-index: 3;
  width: 92px;
  min-width: 92px;
  height: 46px;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  background-color: #f7f7f7;
  color: #555;
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const TimeHeader = styled.th`
  height: 46px;
  border: 1px solid #eeeeee;
  border-radius: 5px;
  background-color: #f7f7f7;
  text-align: center;
`;

const TimeValue = styled.span`
  display: block;
  color: #444;
  font-size: var(--font-size-13);
  font-weight: 600;
  line-height: 1.2;
`;

const TimeLabel = styled.span`
  display: block;
  margin-top: 2px;
  color: #999;
  font-size: var(--font-size-11);
  font-weight: 400;
`;

const BudgetWeekRow = styled.tr`
  ${({$invalid}) =>
    $invalid &&
    `
      td {
        border-color: #e03131;
        background-color: #fff5f5;
      }
    `}
`;

const WeekHeader = styled.th`
  position: sticky;
  left: 0;
  z-index: 2;
  width: 92px;
  min-width: 92px;
  height: 44px;
  padding: 5px 6px;
  border: 1px solid #eeeeee;
  border-radius: 5px;
  background-color: #f7f7f7;
  text-align: center;
`;

const WeekName = styled.span`
  display: block;
  color: #444;
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const WeekTotal = styled.span`
  display: inline-flex;
  min-width: 42px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-top: 3px;
  padding: 0 5px;
  border-radius: 9px;
  background-color:
    ${({$valid, $hasValue}) => {
  if ($valid) {
    return "#eaf7ee";
  }

  if ($hasValue) {
    return "#fff0f0";
  }

  return "#eeeeee";
}};
  color:
    ${({$valid, $hasValue}) => {
  if ($valid) {
    return "#2b8a3e";
  }

  if ($hasValue) {
    return "#e03131";
  }

  return "#999";
}};
  font-size: var(--font-size-11);
  font-weight: 700;
`;

const BudgetCell = styled.td`
  position: relative;
  height: 44px;
  padding: 0;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  background-color:
    ${({$readOnly}) =>
    $readOnly
        ? "#f5f5f5"
        : "#fff"};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:focus-within {
    border-color: #f5811f;
    background-color: #fff8f1;
  }

  &:hover {
    border-color:
      ${({$readOnly}) =>
    $readOnly
        ? "#e5e5e5"
        : "#f5811f"};
  }
`;

const PercentInput = styled.input`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 0 15px 0 4px;
  border: 0;
  outline: 0;
  background-color: transparent;
  color: #444;
  font-size: var(--font-size-13);
  font-weight: 500;
  text-align: center;

  &:read-only {
    color: #999;
    cursor: default;
  }
`;

const PercentUnit = styled.span`
  position: absolute;
  top: 50%;
  right: 4px;
  color: #aaa;
  font-size: var(--font-size-11);
  transform: translateY(-50%);
  pointer-events: none;
`;

const BudgetStatusGuide = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 10px;
`;

const StatusItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #888;
  font-size: var(--font-size-13);
`;

const StatusMark = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color:
    ${({$type}) =>
    $type === "valid"
        ? "#2b8a3e"
        : "#e03131"};
`;